import { useEffect, useImperativeHandle } from 'react';
import styled from 'styled-components';

import ExternalAttachment from 'components/shared/ExternalAttachment';
import FileDropZone from 'components/shared/FileDropZone';
import Upload from 'hooks/useDirectUpload/Upload';
import useDirectUpload from 'hooks/useDirectUpload/useDirectUpload';
import colors from 'styles/theme/colors';
import { Label } from 'styles/typography';
import ClipIcon from 'svg/AttachFileIcon';

import InternalAttachment from '../InternalAttachment';

export type FileInputHandle = {
  reset: () => void;
  // For a consumer that draws the upload list itself (uploadList: 'none') and so owns the
  // remove control too — the uploads live in here, so removal has to come back through.
  removeUpload: (id: string) => void;
};

type FileInputComponentProps = {
  ref?: React.Ref<FileInputHandle>;
  allowedTypes?: { [key: string]: string[] };
  onChange?: (uploads: Upload[]) => void;
  onUploading?: (uploading: boolean) => void;
  // Where this component draws the uploaded files:
  //
  //   'outside' (default) — a list of rows beneath the dropzone
  //   'inside'            — chips within the dropzone, replacing its prompt
  //   'none'              — nowhere; the consumer draws them, because it has other rows to
  //                         interleave (the referral packet shows documents already attached
  //                         alongside the new ones, and one list is the only way they read as one
  //                         packet). `onChange` still reports them.
  //
  // One prop rather than two booleans: this was `renderUploadsInternally` plus `renderUploadList`,
  // three states spelled with two flags whose names read as synonyms — easy for the next consumer
  // to combine wrongly (raised in review on OLIO-2773).
  uploadList?: 'inside' | 'outside' | 'none';
  onDeleteUpload?: (id: string) => void;
  onSetDocType?: (id: string, docType: string | null) => void;
  defaultDocType?: string;
  initialUploads?: Upload[];
  draggingStyles?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  hideLabel?: boolean;
  Icon?: React.ElementType<React.SVGProps<SVGSVGElement>>;
  // How many more files this input may take, and how large each may be. A consumer that already holds
  // files elsewhere (an edited record's kept ones) passes what is left. 0 takes no more.
  maxFiles?: number;
  maxFileSizeBytes?: number;
  // Shown once maxFiles is reached, and as the toast for a drop past it. Defaults to naming maxFiles,
  // which reads wrong when that is only what is left of a larger allowance.
  limitMessage?: string;
};

export default function FileInputComponent(props: FileInputComponentProps) {
  const {
    ref,
    allowedTypes,
    onChange,
    onUploading,
    uploadList = 'outside',
    onDeleteUpload,
    onSetDocType,
    defaultDocType,
    initialUploads = [],
    draggingStyles = {
      backgroundColor: 'var(--primary-blue-05)',
      borderColor: 'var(--primary-blue)',
    },
    containerStyle,
    hideLabel = false,
    Icon = () => <ClipIcon height={16} />,
    maxFiles = 10,
    maxFileSizeBytes,
    limitMessage,
  } = props;

  const { uploadFiles, uploads, uploading, removeUpload, setDocType, resetUploads } = useDirectUpload(
    [...initialUploads],
    {
      defaultDocType,
    }
  );

  useImperativeHandle(ref, () => ({ reset: resetUploads, removeUpload }), [resetUploads, removeUpload]);

  useEffect(() => {
    onChange?.(uploads);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads]);

  useEffect(() => {
    onUploading?.(uploading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  const handleDelete = (id: string) => {
    removeUpload(id);
    onDeleteUpload?.(id);
  };

  const handleSetDocType = (id: string, docType: string | null) => {
    setDocType(id, docType);
    onSetDocType?.(id, docType);
  };

  const canAcceptMoreFiles = uploads.length < maxFiles;

  return (
    <>
      <FileDropZone
        onFilesSelected={uploadFiles}
        noClick
        noDrag={false}
        currentFileCount={uploads.length}
        maxFiles={maxFiles}
        maxFileSizeBytes={maxFileSizeBytes}
        tooManyFilesMessage={limitMessage}
        allowedTypes={allowedTypes}>
        {(dropzone) => (
          <Container
            {...dropzone.getRootProps()}
            onClick={canAcceptMoreFiles ? dropzone.open : () => {}}
            $active={canAcceptMoreFiles && dropzone.isDragActive}
            $canAcceptMoreFiles={canAcceptMoreFiles}
            $draggingStyles={draggingStyles}
            style={containerStyle}>
            <input {...dropzone.getInputProps()} />
            {uploadList === 'inside' && uploads.length > 0 ? (
              <FilesContainer>
                {uploads.map((upload) => (
                  <InternalAttachment key={upload.id} upload={upload} handleDelete={handleDelete} />
                ))}
              </FilesContainer>
            ) : (
              <EmptyStateContainer>
                <Icon />
                {!hideLabel && (
                  <InputLabel>
                    Drag and drop your file(s) here, or&nbsp;
                    <FileBrowserLink aria-label='launch file browser'>browse your files</FileBrowserLink>
                  </InputLabel>
                )}
              </EmptyStateContainer>
            )}

            {/* Shown whatever the list placement: the cap is about the dropzone, not about where
                the files are drawn. It used to be gated on the internal-chips flag, which quietly
                took it away from the referral packet when that moved its list out (OLIO-2773) —
                leaving the browse path to no-op at the cap with nothing said. */}
            {!canAcceptMoreFiles && (
              <LimitContainer>
                <LimitMessage>{limitMessage ?? `A maximum of ${maxFiles} files can be provided`}</LimitMessage>
              </LimitContainer>
            )}
          </Container>
        )}
      </FileDropZone>
      {uploadList === 'outside' && (
        <AttachmentContainer>
          <>
            {uploads.map((upload) => (
              <ExternalAttachment
                key={upload.id}
                error={upload.hasError}
                attachment={upload}
                uploadProgress={upload.progress}
                onDeleteAttachment={handleDelete}
                onDocumentTypeChange={handleSetDocType}
              />
            ))}
          </>
        </AttachmentContainer>
      )}
    </>
  );
}

const Container = styled.div<{ $active: boolean; $canAcceptMoreFiles: boolean; $draggingStyles: React.CSSProperties }>`
  background-color: ${({ $active, $draggingStyles }) => ($active ? $draggingStyles.backgroundColor : 'white')};
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border-width: 1px;
  border-style: dashed;
  border-color: ${({ $active, $draggingStyles }) => ($active ? $draggingStyles.borderColor : 'var(--black-25)')};
  position: relative;
  display: flex;
  min-height: 120px;
  flex-direction: column;

  ${({ $canAcceptMoreFiles, $draggingStyles }) =>
    $canAcceptMoreFiles &&
    `
    cursor: pointer;

    &:hover {
      background-color: ${$draggingStyles.backgroundColor};
      border-color: ${$draggingStyles.borderColor};
    }
  `}

  &:focus-visible {
    outline: none;
  }
`;

const AttachmentContainer = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex: 1;
  padding: 0 16px;
`;

const FileBrowserLink = styled.a`
  color: var(--primary-blue);
  cursor: pointer;
  text-decoration: underline;
`;

const InputLabel = styled(Label)`
  margin-top: 8px;
`;

const FilesContainer = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
  height: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  align-content: flex-start;
  flex-wrap: wrap;
  padding: 16px;
`;

const LimitContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding-bottom: 16px;
`;

const LimitMessage = styled.p`
  font-size: 12px;
  font-weight: normal;
  color: ${colors.black100};
  font-style: italic;
`;
